import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "competenceScore",
    "totalScore",
    "form",
    "masteryRadio",
    "masteryCircle",
    "masteryGroup",
    "scoreInput",
    "absentCheckbox",
  ];

  connect() {
    this.setupFormValidation();
    this.initializeCompetenceScores();
    this.initializeAbsentStates();
    this.initializeKindergartenScores();

    // Delay highlighting to ensure DOM is fully loaded
    setTimeout(() => {
      this.highlightSelectedMastery();
    }, 100);
  }

  setupFormValidation() {
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", (event) => {
        // Calculate kindergarten scores before submission
        this.calculateAllKindergartenScores();

        if (
          !this.validateCompetenceScores() ||
          !this.validateMasterySelections()
        ) {
          event.preventDefault();
        }
      });
    }
  }

  initializeCompetenceScores() {
    if (this.hasCompetenceScoreTargets) {
      // Wait for next tick to ensure all inputs are properly loaded
      setTimeout(() => {
        this.competenceScoreTargets.forEach((input) => {
          // Ensure the input has a value before calculating
          if (input.value) {
            this.updateTotalScore({ target: input });
          }
        });
      }, 0);
    }
  }

  initializeKindergartenScores() {
    // Delay to ensure DOM is fully loaded
    setTimeout(() => {
      this.calculateAllKindergartenScores();
    }, 200);
  }

  calculateAllKindergartenScores() {
    // Find all mastery radios in the form
    const allMasteryRadios = this.element.querySelectorAll(
      'input[type="radio"][data-student-input][data-grades-form-target="masteryRadio"]',
    );

    if (allMasteryRadios.length === 0) return;

    // Get all unique student IDs from mastery radios
    const studentIds = new Set();
    allMasteryRadios.forEach((radio) => {
      const studentId = radio.dataset.studentInput;
      if (studentId) {
        studentIds.add(studentId);
      }
    });

    // Calculate and set scores for each student
    studentIds.forEach((studentId) => {
      const mainScoreInput = document.getElementById(`main_score_${studentId}`);
      if (!mainScoreInput) {
        console.warn(`Main score input not found for student ${studentId}`);
        return;
      }

      const studentMasteryRadios = this.element.querySelectorAll(
        `input[type="radio"][data-student-input="${studentId}"][data-grades-form-target="masteryRadio"]`,
      );

      let total = 0;
      studentMasteryRadios.forEach((radio) => {
        if (radio.checked && !radio.disabled) {
          const value = parseFloat(radio.value) || 0;
          total += value;
        }
      });

      // Always set the value, even if 0 (to ensure it's submitted)
      mainScoreInput.value = total.toFixed(2);
      console.log(
        `Calculated score for student ${studentId}: ${total.toFixed(2)}`,
      );
    });
  }

  validateCompetenceScores() {
    if (!this.hasCompetenceScoreTargets) return true;

    // Group inputs by student ID
    const studentInputs = {};
    this.competenceScoreTargets.forEach((input) => {
      if (input.disabled) {
        input.classList.remove("border-red-500");
        return;
      }
      const studentId = input.dataset.studentId;
      if (!studentInputs[studentId]) {
        studentInputs[studentId] = [];
      }
      studentInputs[studentId].push(input);
    });

    // For each student: if at least one competence is filled,
    // all competences must be filled
    let isValid = true;
    Object.values(studentInputs).forEach((inputs) => {
      const filled = inputs.filter(
        (i) => i.value && i.value.trim() !== "",
      );
      const empty = inputs.filter(
        (i) => !i.value || i.value.trim() === "",
      );

      if (filled.length > 0 && empty.length > 0) {
        // Partial fill: mark empty fields as invalid
        empty.forEach((i) => i.classList.add("border-red-500"));
        isValid = false;
      } else {
        // All filled or all empty: both are valid
        inputs.forEach((i) => i.classList.remove("border-red-500"));
      }
    });

    if (!isValid) {
      alert("Please fill in all competence scores for students you have started grading");
    }

    return isValid;
  }

  validateMasterySelections() {
    if (!this.hasMasteryGroupTargets) return true;

    let isValid = true;
    let unselectedGroups = [];

    this.masteryGroupTargets.forEach((group) => {
      const radios = group.querySelectorAll(
        '[data-grades-form-target="masteryRadio"]',
      );
      const activeRadios = Array.from(radios).filter(
        (radio) => !radio.disabled,
      );
      if (activeRadios.length === 0) {
        group.classList.remove(
          "border-2",
          "border-red-500",
          "rounded-md",
          "p-2",
        );
        return;
      }
      const hasSelection = activeRadios.some((radio) => radio.checked);

      if (!hasSelection) {
        isValid = false;
        // Add visual indication for unselected groups
        group.classList.add("border-2", "border-red-500", "rounded-md", "p-2");
        unselectedGroups.push(group);
      } else {
        // Remove error styling if previously added
        group.classList.remove(
          "border-2",
          "border-red-500",
          "rounded-md",
          "p-2",
        );
      }
    });

    if (!isValid) {
      alert(
        "Please evaluate all competences by selecting a mastery level for each.",
      );
    }

    return isValid;
  }

  validateScore(event) {
    this.enforceScoreLimits(event.target, parseFloat(event.target.dataset.scale));

    const input = event.target;
    // Remove red border if value is now valid
    if (input.value && input.value.trim() !== "") {
      input.classList.remove("border-red-500");
    }

    this.updateTotalScore(event);
  }

  // Only an out-of-range value is rewritten: a blank field stays blank (it is
  // not a zero), and a value being typed, such as "12.0" on the way to "12.05",
  // keeps its text.
  enforceScoreLimits(input, maxScore) {
    const value = parseFloat(input.value);
    if (isNaN(value)) return;

    if (value > maxScore) {
      input.value = maxScore;
    } else if (value < 0) {
      input.value = 0;
    }
  }

  updateTotalScore(event) {
    const input = event.target;
    const studentId = input.dataset.studentId;
    const mainScoreInput = document.getElementById(input.dataset.mainScoreId);
    const competenceScoreInputs = document.querySelectorAll(
      `.competence_scores_${studentId}`,
    );

    let total = 0;
    let filled = false;
    competenceScoreInputs.forEach((input) => {
      filled = filled || input.value.trim() !== "";
      const value = parseFloat(input.value) || 0;
      total += value;
    });

    // Update the main score input with the total; no competence, no mark
    if (mainScoreInput) {
      mainScoreInput.value = filled ? total.toFixed(2) : "";
    }
  }

  selectMastery(event) {
    // Only update the group for the clicked competence
    const group = event.currentTarget.closest(
      '[data-grades-form-target="masteryGroup"]',
    );
    const radios = group.querySelectorAll(
      '[data-grades-form-target="masteryRadio"]',
    );
    const circles = group.querySelectorAll(
      '[data-grades-form-target="masteryCircle"]',
    );
    const value = event.currentTarget.dataset.value;

    // Clear any validation error styling
    group.classList.remove("border-2", "border-red-500", "rounded-md", "p-2");

    // Uncheck all radios and remove backgrounds in this group
    radios.forEach((radio, idx) => {
      radio.checked = false;
      circles[idx].classList.remove("bg-cta", "bg-yellow-400", "bg-red-400");
    });

    // Check the clicked radio and add background
    const clickedRadio = event.currentTarget.querySelector(
      'input[type="radio"]',
    );
    const clickedCircle = event.currentTarget.querySelector(
      '[data-grades-form-target="masteryCircle"]',
    );

    if (clickedRadio && clickedCircle) {
      clickedRadio.checked = true;

      // Add the correct background
      console.log("this is value", value);
      if (value == "2") {
        clickedCircle.classList.add("bg-cta");
      } else if (value == "1") {
        clickedCircle.classList.add("bg-yellow-400");
      } else if (value == "0") {
        clickedCircle.classList.add("bg-red-400");
      }

      // Calculate and update the main score for kindergarten
      this.updateKindergartenTotalScore(clickedRadio);
    }
  }

  updateKindergartenTotalScore(clickedRadio) {
    // Get the student ID from the clicked radio
    const studentId = clickedRadio.dataset.studentInput;
    if (!studentId) {
      console.warn("No student ID found on clicked radio");
      return;
    }

    // Find the main score input for this student
    const mainScoreInput = document.getElementById(`main_score_${studentId}`);
    if (!mainScoreInput) {
      console.warn(`Main score input not found for student ${studentId}`);
      return;
    }

    // Find all checked mastery radios for this student
    const allMasteryRadios = this.element.querySelectorAll(
      `input[type="radio"][data-student-input="${studentId}"][data-grades-form-target="masteryRadio"]`,
    );

    // Sum all checked radio values
    let total = 0;
    allMasteryRadios.forEach((radio) => {
      if (radio.checked && !radio.disabled) {
        const value = parseFloat(radio.value) || 0;
        total += value;
      }
    });

    // Update the main score input
    mainScoreInput.value = total.toFixed(2);
    console.log(`Updated score for student ${studentId}: ${total.toFixed(2)}`);
  }

  toggleAbsent(event) {
    this.applyAbsentState(event.target);
  }

  initializeAbsentStates() {
    if (!this.hasAbsentCheckboxTarget) return;

    this.absentCheckboxTargets.forEach((checkbox) => {
      this.applyAbsentState(checkbox);
    });
  }

  applyAbsentState(checkbox) {
    const studentId = checkbox.dataset.studentId;
    if (!studentId) return;

    const isAbsent = checkbox.checked;
    const inputs = this.element.querySelectorAll(
      `[data-student-input="${studentId}"]`,
    );
    const wrapper = this.element.querySelector(
      `[data-student-input-wrapper="${studentId}"]`,
    );

    inputs.forEach((input) => {
      input.disabled = isAbsent;
      if (isAbsent) {
        this.clearInputValue(input);
        input.classList?.remove("border-red-500");
      }
    });

    // Clear main score when absent
    if (isAbsent) {
      const mainScoreInput = document.getElementById(`main_score_${studentId}`);
      if (mainScoreInput) {
        mainScoreInput.value = "";
      }
    }

    if (wrapper) {
      wrapper.classList.toggle("opacity-50", isAbsent);
      wrapper.classList.toggle("pointer-events-none", isAbsent);
    }

    this.highlightSelectedMastery();
  }

  clearInputValue(input) {
    if (input.type === "radio") {
      input.checked = false;
      return;
    }

    if (input.type === "hidden") {
      input.value = "";
      return;
    }

    if (typeof input.value !== "undefined") {
      input.value = "";
    }
  }

  // Method to re-highlight after dynamic content changes
  refreshHighlighting() {
    this.highlightSelectedMastery();
  }

  highlightSelectedMastery() {
    // Use a robust selector to find all mastery groups
    const groups = this.element.querySelectorAll(
      '[data-grades-form-target="masteryGroup"]',
    );

    console.log(`Found ${groups.length} mastery groups for highlighting`);

    groups.forEach((group, groupIndex) => {
      const radios = group.querySelectorAll(
        '[data-grades-form-target="masteryRadio"]',
      );
      const circles = group.querySelectorAll(
        '[data-grades-form-target="masteryCircle"]',
      );

      console.log(
        `Group ${groupIndex}: ${radios.length} radios, ${circles.length} circles`,
      );

      radios.forEach((radio, idx) => {
        const circle = circles[idx];
        if (!circle) return;

        // Clear all background colors first
        circle.classList.remove("bg-cta", "bg-yellow-400", "bg-red-400");

        // Apply background if radio is checked
        if (radio.checked) {
          console.log(
            `Radio at index ${idx} is checked with value: ${radio.value}`,
          );
          if (radio.value == "2") {
            circle.classList.add("bg-cta");
            console.log("Applied green background");
          } else if (radio.value == "1") {
            circle.classList.add("bg-yellow-400");
            console.log("Applied yellow background");
          } else if (radio.value == "0") {
            circle.classList.add("bg-red-400");
            console.log("Applied red background");
          }
        } else {
          console.log(
            `Radio at index ${idx} is NOT checked (value: ${radio.value})`,
          );
        }
      });
    });
  }

  validateSecondaryScore(event) {
    this.enforceScoreLimits(event.target, parseFloat(event.target.dataset.maxScore));
  }
}
